// services/mic-stt.ts — Deepgram STT, mic stream management, speech evaluation

import { store } from '../store';
import { setIsMyTurn, addProblemLine, advanceLine, setLineProgress, resetLineProgress } from '../store/practiceSlice';
import { normalizeText, calculateSimilarity, checkKeyWords } from '../lib/evaluation';
import { speakFeedback } from './speech';
import { getWorkingLines, getCurrentActualIndex } from '../features/OptionsMenu';

const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

export enum MicErrorType {
    NONE = 'none',
    PERMISSION_DENIED = 'permission_denied',
    NOT_FOUND = 'not_found',
    NOT_SUPPORTED = 'not_supported',
    WEBSOCKET_FAILED = 'websocket_failed',
    TOKEN_FAILED = 'token_failed',
    UNKNOWN = 'unknown'
}

let sessionMicStream: MediaStream | null = null;
let sessionMicPromise: Promise<MediaStream | null> | null = null;
let micPermissionDenied = false;
let isMicStarting = false;
let deepgramSocket: WebSocket | null = null;
let lastSpeechTime = 0;
let sttMediaRecorder: MediaRecorder | null = null;
let heardText = '';
let fullHeardText = '';
let speechTimeout: ReturnType<typeof setTimeout> | null = null;
let currentMicError: MicErrorType = MicErrorType.NONE;
let micErrorMessage: string = '';
let currentLineChunks: string[] = [];
let coveredChunks: Set<number> = new Set();

export function getSpeechRecognition() {
    return SpeechRecognition;
}

function splitIntoChunks(text: string): string[] {
    // Split line into meaningful phrases based on punctuation
    // This creates natural speaking chunks for tracking progress
    const chunks: string[] = [];

    // First, try splitting on major punctuation (periods, commas, dashes, semicolons)
    const parts = text.split(/([.,;—–-]+)/);
    let currentChunk = '';

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();
        if (!part) continue;

        // If it's punctuation, add to current chunk
        if (/^[.,;—–-]+$/.test(part)) {
            currentChunk += part;
        } else {
            // Add the text part
            if (currentChunk) {
                currentChunk += ' ' + part;
            } else {
                currentChunk = part;
            }

            // Check if we should create a chunk here
            const nextIsPunctuation = i + 1 < parts.length && /^[.,;—–-]+$/.test(parts[i + 1].trim());
            if (nextIsPunctuation || currentChunk.split(/\s+/).length >= 8) {
                // Chunk is complete
                if (currentChunk.trim()) {
                    chunks.push(currentChunk.trim());
                }
                currentChunk = '';
            }
        }
    }

    // Add any remaining text
    if (currentChunk.trim()) {
        chunks.push(currentChunk.trim());
    }

    // If no chunks created (no punctuation), split by word count
    if (chunks.length === 0) {
        const words = text.split(/\s+/);
        for (let i = 0; i < words.length; i += 8) {
            chunks.push(words.slice(i, i + 8).join(' '));
        }
    }

    return chunks.filter(c => c.length > 0);
}

export function getLineProgress(): { chunks: string[], covered: number[], percentage: number } {
    return {
        chunks: currentLineChunks,
        covered: Array.from(coveredChunks),
        percentage: currentLineChunks.length > 0 ? coveredChunks.size / currentLineChunks.length : 0
    };
}

export function initializeLineTracking(lineText: string): void {
    currentLineChunks = splitIntoChunks(lineText);
    coveredChunks.clear();
    console.log('Line chunks:', currentLineChunks);
}

function updateChunkTracking(spokenText: string): void {
    if (currentLineChunks.length === 0) return;

    const normalized = normalizeText(spokenText);

    // Check each chunk to see if it's been covered
    currentLineChunks.forEach((chunk, index) => {
        if (coveredChunks.has(index)) return; // Already covered

        const normalizedChunk = normalizeText(chunk);
        const similarity = calculateSimilarity(normalized, normalizedChunk);

        // Mark as covered if similarity > 50% or if key words are present
        if (similarity > 0.5 || checkKeyWords(chunk, spokenText) > 0.5) {
            coveredChunks.add(index);
            console.log(`Chunk ${index + 1}/${currentLineChunks.length} covered: "${chunk}"`);
        }
    });

    // Dispatch progress update to Redux
    store.dispatch(setLineProgress({
        chunks: currentLineChunks,
        covered: Array.from(coveredChunks),
        percentage: coveredChunks.size / currentLineChunks.length
    }));
}

export function getMicError(): { type: MicErrorType; message: string } {
    return { type: currentMicError, message: micErrorMessage };
}

export async function checkMicPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unsupported'> {
    if (!navigator.permissions?.query) return 'unsupported';
    try {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        return result.state;
    } catch {
        return 'unsupported';
    }
}

export async function ensureSessionMicStream(): Promise<MediaStream | null> {
    if (sessionMicStream && sessionMicStream.getTracks().some(t => t.readyState === 'live')) {
        return sessionMicStream;
    }
    if (micPermissionDenied) return null;
    if (sessionMicPromise) return sessionMicPromise;

    // Check browser support
    if (!navigator.mediaDevices?.getUserMedia) {
        currentMicError = MicErrorType.NOT_SUPPORTED;
        micErrorMessage = 'Microphone not supported. Try Chrome or Safari.';
        return null;
    }

    sessionMicPromise = navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
            sessionMicStream = stream;
            micPermissionDenied = false;
            currentMicError = MicErrorType.NONE;
            micErrorMessage = '';
            return stream;
        })
        .catch(err => {
            console.log('Session mic access error:', err);
            micPermissionDenied = true;
            if (err.name === 'NotAllowedError') {
                currentMicError = MicErrorType.PERMISSION_DENIED;
                micErrorMessage = 'Mic denied. Enable in Settings → Safari → runlines.';
            } else if (err.name === 'NotFoundError') {
                currentMicError = MicErrorType.NOT_FOUND;
                micErrorMessage = 'No microphone found.';
            } else if (err.name === 'NotSupportedError') {
                currentMicError = MicErrorType.NOT_SUPPORTED;
                micErrorMessage = 'Mic requires HTTPS or localhost.';
            } else {
                currentMicError = MicErrorType.UNKNOWN;
                micErrorMessage = `Mic error: ${err.message}`;
            }
            return null;
        })
        .finally(() => {
            sessionMicPromise = null;
        });

    return sessionMicPromise;
}

export function releaseSessionMic() {
    if (sessionMicStream) {
        sessionMicStream.getTracks().forEach(t => t.stop());
        sessionMicStream = null;
    }
}

export async function startMic() {
    const { micEnabled } = store.getState().settings;
    if (deepgramSocket && deepgramSocket.readyState === WebSocket.OPEN) return;
    if (isMicStarting || !micEnabled) return;
    isMicStarting = true;

    const stream = await ensureSessionMicStream();
    if (!stream) {
        isMicStarting = false;
        return;
    }

    let dgKey;
    try {
        const tokenRes = await fetch('/api/deepgram-token', { method: 'POST' });
        if (!tokenRes.ok) throw new Error('Token fetch failed');
        const tokenData = await tokenRes.json();
        dgKey = tokenData.key;
    } catch (e) {
        console.log('Deepgram token error:', e);
        currentMicError = MicErrorType.TOKEN_FAILED;
        micErrorMessage = 'Speech recognition token fetch failed.';
        isMicStarting = false;
        return;
    }

    // Increased endpointing to 2500ms (2.5 seconds) to allow for natural pauses in longer lines
    const wsUrl = `wss://api.deepgram.com/v1/listen?model=nova-2&language=en-US&interim_results=true&endpointing=2500`;
    const ws = new WebSocket(wsUrl, ['token', dgKey]);
    deepgramSocket = ws;

    ws.onopen = () => {
        console.log('Deepgram connected');
        isMicStarting = false;
        sttMediaRecorder = new MediaRecorder(stream);
        sttMediaRecorder.ondataavailable = (e) => {
            if (ws.readyState === WebSocket.OPEN && e.data.size > 0) {
                ws.send(e.data);
            }
        };
        sttMediaRecorder.start(250);
    };

    ws.onmessage = (event) => {
        const state = store.getState();
        if (!state.settings.micEnabled || !state.practice.isPracticing || state.practice.isPaused) return;
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        const alt = msg?.channel?.alternatives?.[0];
        if (!alt) return;
        const transcript = alt.transcript?.trim();
        if (!transcript) return;
        const isFinal = msg.is_final;
        lastSpeechTime = Date.now();
        if (isFinal) {
            fullHeardText = (fullHeardText + ' ' + transcript).trim();
            heardText = transcript;
        } else {
            heardText = transcript;
        }

        // Update chunk tracking with what's been said so far
        updateChunkTracking(fullHeardText);

        if (speechTimeout) clearTimeout(speechTimeout);
        // Add grace period even for "final" results to allow for mid-line pauses
        // Increased timeouts: 6 seconds for relaxed mode, 8 seconds for perfect mode
        const waitTime = state.settings.mode === 'perfect' ? 8000 : 6000;
        speechTimeout = setTimeout(() => {
            const s = store.getState();
            if (s.practice.isMyTurn && s.practice.isPracticing && s.settings.micEnabled) {
                evaluateResponse(isFinal);
            }
        }, waitTime);
    };

    ws.onerror = () => {
        currentMicError = MicErrorType.WEBSOCKET_FAILED;
        micErrorMessage = 'Speech recognition connection failed.';
    };

    ws.onclose = (event) => {
        deepgramSocket = null;
        sttMediaRecorder = null;
        if (event.code !== 1000 && currentMicError === MicErrorType.NONE) {
            currentMicError = MicErrorType.WEBSOCKET_FAILED;
            micErrorMessage = 'Speech recognition disconnected.';
        }
    };
}

export function stopMic() {
    if (sttMediaRecorder) {
        try { sttMediaRecorder.stop(); } catch {}
        sttMediaRecorder = null;
    }
    if (deepgramSocket) {
        deepgramSocket.close();
        deepgramSocket = null;
    }
}

export function evaluateResponse(force = false) {
    const state = store.getState();
    if (!state.practice.isMyTurn) return;
    const workingLines = getWorkingLines();
    const actualIndex = getCurrentActualIndex();
    const line = workingLines[actualIndex];
    if (!line) return;
    const expected = line.dialogue;
    const expectedWordCount = normalizeText(expected).split(/\s+/).filter(Boolean).length;
    const wordCount = fullHeardText.trim().split(/\s+/).filter(w => w.length > 0).length;
    if (!force) {
        const minWords = expectedWordCount <= 2 ? 1 : 2;
        if (wordCount < minWords) return;
    }
    if (state.settings.mode === 'perfect') {
        checkWordPerfect(expected, fullHeardText);
    } else {
        checkRelaxed(expected, fullHeardText);
    }
    heardText = '';
    fullHeardText = '';
}

function checkWordPerfect(expected: string, heard: string) {
    const expectedWords = normalizeText(expected).split(/\s+/);
    const heardWords = normalizeText(heard).split(/\s+/);
    let allCorrect = true;
    for (let i = 0; i < expectedWords.length; i++) {
        if (expectedWords[i] !== (heardWords[i] || '')) { allCorrect = false; break; }
    }
    if (allCorrect) {
        store.dispatch(setIsMyTurn(false));
        setTimeout(() => store.dispatch(advanceLine()), 1000);
    } else {
        const actualIndex = getCurrentActualIndex();
        if (actualIndex !== undefined) store.dispatch(addProblemLine(actualIndex));
        const firstDiff = expectedWords.findIndex((w, i) => w !== (heardWords[i] || ''));
        if (firstDiff >= 0) speakFeedback(`The word was "${expectedWords[firstDiff]}". Try that again.`);
        fullHeardText = '';
        heardText = '';
    }
}

function checkRelaxed(expected: string, heard: string) {
    const similarity = calculateSimilarity(normalizeText(heard), normalizeText(expected));
    const keyWordsMatch = checkKeyWords(expected, heard);
    store.dispatch(setIsMyTurn(false));
    setTimeout(() => store.dispatch(advanceLine()), similarity >= 0.5 || keyWordsMatch >= 0.4 ? 300 : 0);
}
